"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { MIN_DAYS_FOR_PROJECTION } from "@/lib/capital/eligibility";

/**
 * Hero de la página pública de Redy Capital.
 *
 * 🔴 El encuadre NO es "Redy te presta". Es "tu operación es tu historial
 * crediticio": Redy convierte las ventas que ya mide en el dato que un
 * prestamista pide primero. Eso es cierto, es la diferencia real frente a
 * Clover —que tiene los datos del comercio y aun así le muestra criterios
 * genéricos— y no afirma en ningún momento que Redy preste dinero.
 *
 * Importa por dos razones que no son cosméticas: Redy Commerce LLC se
 * constituyó sobre la tesis de que Redy NO toca dinero, y la marca REDY vive
 * hoy en clase 42 en manos de una ASEGURADORA. Empujar públicamente un mensaje
 * de "somos tu financiera" agrava las dos cosas a la vez.
 */
export default function CapitalHero() {
  const { t } = useLang();

  // El anillo del hero es ilustrativo: enseña cómo se ve el contador dentro
  // del panel. Un número fijo, no un dato de nadie.
  const demoDay = 74;
  const percent = Math.round((demoDay / MIN_DAYS_FOR_PROJECTION) * 100);
  const size = 168;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;

  return (
    <section className="relative pt-36 pb-20 px-6 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[760px] h-[420px] bg-redy-orange/6 rounded-full blur-[130px] pointer-events-none" />

      <div className="max-w-[1200px] mx-auto relative z-10">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-5">
              {t("Redy Capital", "Redy Capital")}
            </div>
            <h1 className="text-5xl md:text-[68px] font-black tracking-tight leading-[1.02] text-redy-charcoal">
              {/* El quiebre va tras "operation is": partir después de "your"
                  dejaba esa palabra huérfana en su propia línea a 1280px. */}
              {t("Your operation is", "Tu operación es")}
              <br />
              {t("your credit history.", "tu historial crediticio.")}
            </h1>
            <p className="text-lg md:text-xl text-redy-secondary max-w-[560px] leading-relaxed font-medium mt-6">
              {t(
                "Lenders turn restaurants away because they cannot read their cash flow. Redy already reads it — every order, every day. After 90 days selling on Redy we estimate your annual run rate and show you which financing options your real numbers reach.",
                "Los prestamistas rechazan restaurantes porque no saben leer su flujo de caja. Redy ya lo lee — cada orden, cada día. A los 90 días vendiendo en Redy estimamos tu corrida anual y te mostramos qué opciones de financiamiento alcanzan tus números reales."
              )}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-9">
              <Link
                href="/demo"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-[15px] font-bold text-white transition-transform hover:scale-[1.02]"
                style={{ background: "linear-gradient(90deg,#D7263D,#F4722B)" }}
              >
                {t("Start with Redy", "Empieza con Redy")}
              </Link>
              <a
                href="#how"
                className="px-6 py-4 rounded-xl text-[15px] font-bold text-redy-charcoal hover:bg-black/[0.04] transition-colors"
              >
                {t("See how it works", "Ver cómo funciona")}
              </a>
            </div>

            <p className="text-[12.5px] text-redy-secondary/70 mt-6 max-w-[520px] leading-relaxed">
              {t(
                "Redy is not a lender. Financing is provided by SmallBusinessLoans, North's financing partner, who evaluates and decides independently.",
                "Redy no es un prestamista. El financiamiento lo provee SmallBusinessLoans, socio de financiamiento de North, que evalúa y decide de forma independiente."
              )}
            </p>
          </motion.div>

          {/* Contador tal como se ve dentro del panel */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="flex justify-center lg:justify-end"
          >
            <div className="relative">
              <div className="absolute inset-0 m-auto w-[300px] h-[300px] bg-redy-orange/12 rounded-full blur-[90px]" />
              <div className="relative w-[320px] rounded-[24px] border border-white/8 overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.32)]" style={{ background: "#141414" }}>
                <div className="h-[3px]" style={{ background: "linear-gradient(90deg,#D7263D,#F4722B)" }} />
                <div className="p-7">
                  <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-redy-orange">
                    {t("Redy Capital", "Redy Capital")}
                  </p>

                  <div className="flex justify-center my-6">
                    <div className="relative" style={{ width: size, height: size }}>
                      <svg width={size} height={size} className="-rotate-90" aria-hidden>
                        <defs>
                          <linearGradient id="capital-mkt-ring" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#D7263D" />
                            <stop offset="100%" stopColor="#F4722B" />
                          </linearGradient>
                        </defs>
                        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
                        <motion.circle
                          cx={size / 2}
                          cy={size / 2}
                          r={r}
                          fill="none"
                          stroke="url(#capital-mkt-ring)"
                          strokeWidth={stroke}
                          strokeLinecap="round"
                          strokeDasharray={circumference}
                          initial={{ strokeDashoffset: circumference }}
                          animate={{ strokeDashoffset: circumference * (1 - percent / 100) }}
                          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        />
                      </svg>
                      <div className="absolute inset-0 grid place-items-center text-center leading-none">
                        <div>
                          <p className="text-[46px] font-black text-white tracking-tight">{demoDay}</p>
                          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/40 mt-1.5">
                            / {MIN_DAYS_FOR_PROJECTION}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-[15px] font-bold text-white text-center leading-snug">
                    {t("Your estimate is on its way.", "Tu estimación está en camino.")}
                  </p>
                  <p className="text-[12.5px] text-white/50 text-center mt-1.5 leading-relaxed">
                    {t(
                      "The counter starts with your first sale.",
                      "El contador arranca con tu primera venta."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
